import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { thermalPrint, pairPrinter } from "../../lib/thermalPrinter";
import DangerZoneModal from "../../components/DangerZoneModal";
import NearbyInvoicesModal from "../../components/NearbyInvoicesModal";
import { PrepaidSettlement } from "../../components/payments/PrepaidSettlement";
import { buildInvoiceReceipt } from "../../lib/receiptBuilder";
import {
  Plus, Download, CreditCard, X, ReceiptX, CheckCircle, Clock, ArrowClockwise,
  Link as LinkIcon, Trash, Warning, WhatsappLogo, Envelope, Database, Printer,
  Funnel, FileCsv, FilePdf, Users, MagnifyingGlass, CaretDown,
} from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";

const STATUS_STYLE = {
  unpaid: "bg-[#F9E4B7] text-[#7A5A11]",
  paid: "bg-[#D4E0D1] text-[#1A3317]",
  refunded: "bg-[#F4C7B9] text-[#7A2E18]",
};

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [openPay, setOpenPay] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Iter 178 · Danger-zone modal state.
  const [dzOpen, setDzOpen] = useState(null);
  const [dzBusy, setDzBusy] = useState(false);
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";

  // Iter 238 · Auto-open the Manual Invoice builder pre-filled from an
  // SR when the URL carries ?prefill=<base64-json>.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("prefill");
      if (!p) return;
      const decoded = JSON.parse(decodeURIComponent(atob(p)));
      setPrefill(decoded);
      setOpenNew(true);
      // strip the query so a refresh doesn't reopen it
      const url = new URL(window.location.href);
      url.searchParams.delete("prefill");
      window.history.replaceState({}, "", url.toString());
    } catch { /* ignore malformed prefill */ }
  }, []);

  // Iter 139 — filters + summary + exports
  const [filters, setFilters] = useState({
    date_from: "", date_to: "",
    district_id: "", lsgd_id: "", ward_id: "",
    payment_status: "", search: new URLSearchParams(window.location.search).get("search") || "",
  });
  const [summary, setSummary] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [lsgds, setLsgds] = useState([]);
  const [wards, setWards] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [consolidatedOpen, setConsolidatedOpen] = useState(false);

  const [debouncedSearch, setDebouncedSearch] = useState(() => (new URLSearchParams(window.location.search).get("search") || "").trim());
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search.trim()), 450);
    return () => clearTimeout(t);
  }, [filters.search]);

  const filterQs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries({ ...filters, search: debouncedSearch }).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [filters.district_id, filters.lsgd_id, filters.ward_id, filters.payment_status,
      filters.date_from, filters.date_to, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      const url = filterQs ? `/finance/invoices?${filterQs}` : "/finance/invoices";
      const r = await api.get(url);
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadSummary = async () => {
    try {
      const url = filterQs ? `/finance/invoices/summary?${filterQs}` : "/finance/invoices/summary";
      const r = await api.get(url);
      setSummary(r.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); loadSummary(); }, [filterQs]);

  // Cascading location dropdowns
  useEffect(() => {
    api.get("/locations?type=district")
      .then((r) => setDistricts(r.data || []))
      .catch(() => setDistricts([]));
  }, []);
  useEffect(() => {
    if (!filters.district_id) { setLsgds([]); return; }
    api.get(`/locations?type=lsgd&parent_id=${filters.district_id}`)
      .then((r) => setLsgds(r.data || []))
      .catch(() => setLsgds([]));
  }, [filters.district_id]);
  useEffect(() => {
    if (!filters.lsgd_id) { setWards([]); return; }
    api.get(`/locations?type=ward&parent_id=${filters.lsgd_id}`)
      .then((r) => setWards(r.data || []))
      .catch(() => setWards([]));
  }, [filters.lsgd_id]);

  const applyPreset = (preset) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    let from = "", to = "";
    if (preset === "today") { from = to = iso(today); }
    else if (preset === "month") {
      from = iso(new Date(today.getFullYear(), today.getMonth(), 1));
      to = iso(today);
    } else if (preset === "lastmonth") {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      from = iso(first); to = iso(last);
    } else if (preset === "clear") { from = ""; to = ""; }
    setFilters((f) => ({ ...f, date_from: from, date_to: to }));
  };
  const resetFilters = () => setFilters({
    date_from: "", date_to: "", district_id: "", lsgd_id: "", ward_id: "",
    payment_status: "", search: "",
  });

  const downloadAuthed = async (path, filename, errLabel) => {
    try {
      const token = localStorage.getItem("rescigo_token");
      const res = await fetch(`${API_BASE}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || res.statusText);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) { toast.error(errLabel || "Download failed"); }
  };
  const exportList = (fmt) => {
    const q = filterQs ? `?${filterQs}` : "";
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const filename = `invoices_${stamp}.${fmt}`;
    downloadAuthed(`/finance/invoices/export.${fmt}${q}`, filename,
      `${fmt.toUpperCase()} export failed`);
  };
  const exportConsolidated = (groupBy) => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    p.set("group_by", groupBy);
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const filename = `consolidated_${groupBy}_${stamp}.pdf`;
    downloadAuthed(`/finance/invoices/consolidated.pdf?${p.toString()}`, filename,
      "Consolidated PDF failed");
    setConsolidatedOpen(false);
  };

  const pdf = async (inv, detailed = false, paper = "a4") => {
    try {
      const token = localStorage.getItem("rescigo_token");
      const qs = new URLSearchParams();
      if (detailed) qs.set("detailed", "1");
      if (paper && paper !== "a4") qs.set("paper", paper);
      const url = `${API_BASE}/finance/invoices/${inv.id}/pdf${qs.toString() ? `?${qs.toString()}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const bUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = bUrl;
      const suffix = (detailed ? "-detailed" : "") + (paper !== "a4" ? `-${paper}` : "");
      a.download = `${inv.invoice_no}${suffix}.pdf`;
      a.click();
      URL.revokeObjectURL(bUrl);
      const paperLabel = paper === "thermal" ? "thermal receipt" : paper.toUpperCase();
      toast.success(`Downloaded ${paperLabel}${detailed ? " (detailed)" : ""}`);
    } catch (e) { toast.error("Download failed"); }
  };

  const thermal = async (inv) => {
    // Iter 81 — Bluetooth thermal print. We hydrate the invoice
    // (line items + customer + totals), build an ESC/POS receipt
    // and stream it to the paired 58/80 mm printer over Web
    // Bluetooth. On unsupported browsers or first use we surface a
    // pair-picker; on hard failures we fall back to the browser
    // print view so the user is never stuck.
    const t = toast.loading("Preparing receipt…");
    try {
      const full = (await api.get(`/finance/invoices/${inv.id}`)).data;
      const receipt = buildInvoiceReceipt(full);
      toast.loading("Sending to printer…", { id: t });
      try {
        await thermalPrint(receipt);
        toast.success(`Printed · ${full.invoice_no}`, { id: t });
      } catch (e) {
        if (e?.message === "NEEDS_PAIR" || /No printer/i.test(e?.message || "")) {
          toast.message("Choose your printer", { id: t });
          await pairPrinter();
          await thermalPrint(receipt);
          toast.success(`Printed · ${full.invoice_no}`, { id: t });
        } else if (/not supported|https|Bluetooth/i.test(e?.message || "")) {
          // Browser doesn't support Web Bluetooth — fall back to the
          // HTML thermal preview which uses window.print().
          toast.dismiss(t);
          toast.message("Opening print preview (this browser can't reach Bluetooth)…");
          const w = window.open(`/finance/thermal/${inv.id}`, "_blank");
          if (!w) toast.error("Popup blocked — please allow popups for thermal print");
        } else {
          throw e;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Print failed", { id: t });
    }
  };

  const sendWhatsApp = async (inv, mode) => {
    try {
      const r = await api.post(`/finance/invoices/${inv.id}/whatsapp`, { mode });
      if (mode === "manual" && r.data.url) {
        window.open(r.data.url, "_blank");
        toast.success("WhatsApp opened in new tab");
      } else {
        toast.success(r.data.sent ? "Sent via WhatsApp API" : "Queued");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendEmail = async (inv) => {
    if (!inv.customer?.email) {
      toast.error("Customer has no email on file");
      return;
    }
    try {
      const r = await api.post(`/finance/invoices/${inv.id}/email`);
      toast.success(`Emailed to ${r.data.to}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Invoices" subtitle="Module 12 · Finance"
      actions={
        <div className="flex gap-2">
          {isSuper && (
            <>
              <button data-testid="btn-delete-selected-invoices"
                      disabled={selected.size === 0}
                      onClick={() => setDeleteOpen({ mode: "selected" })}
                      className="inline-flex items-center gap-2 h-10 px-3 border border-[#C25D38] text-[#C25D38] rounded-md text-xs hover:bg-[#F9EDE7] disabled:opacity-40 disabled:cursor-not-allowed">
                <Trash size={12} weight="bold"/>Delete Selected ({selected.size})
              </button>
            </>
          )}
          <div className="relative">
            <button data-testid="btn-export-open" onClick={() => setExportOpen(v => !v)}
                    className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-2 hover:bg-[#F7F6F2]">
              <Download size={13}/>Export <CaretDown size={10}/>
            </button>
            {exportOpen && (
              <div className="absolute right-0 mt-1 w-52 bg-white border border-[#E5E2D9] rounded-md shadow-lg z-30" data-testid="export-menu">
                <button onClick={() => { setExportOpen(false); exportList("csv"); }}
                        data-testid="btn-export-csv"
                        className="w-full text-left px-3 py-2 text-xs hover:bg-[#F7F6F2] inline-flex items-center gap-2">
                  <FileCsv size={14}/> Export CSV
                </button>
                <button onClick={() => { setExportOpen(false); exportList("pdf"); }}
                        data-testid="btn-export-pdf"
                        className="w-full text-left px-3 py-2 text-xs hover:bg-[#F7F6F2] inline-flex items-center gap-2 border-t border-[#F0EDE4]">
                  <FilePdf size={14}/> Export PDF (list)
                </button>
                <button onClick={() => { setExportOpen(false); window.print(); }}
                        data-testid="btn-export-print"
                        className="w-full text-left px-3 py-2 text-xs hover:bg-[#F7F6F2] inline-flex items-center gap-2 border-t border-[#F0EDE4]">
                  <Printer size={14}/> Print current view
                </button>
              </div>
            )}
          </div>
          <button data-testid="btn-consolidated-open"
                  onClick={() => setConsolidatedOpen(true)}
                  className="h-10 px-3 border border-[#0B2B66] text-[#0B2B66] rounded-md text-xs inline-flex items-center gap-2 hover:bg-[#E4EAF5]">
            <Users size={13}/>Consolidated bills
          </button>
          <button data-testid="btn-new-invoice" onClick={() => setOpenNew(true)}
                  className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Plus size={14} weight="bold"/>Manual invoice
          </button>
        </div>
      }
    >
      {/* Iter 139 — Filter bar */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-3 mb-3" data-testid="inv-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">
            <Funnel size={11}/> Filters
          </div>
          {/* Date presets */}
          <button data-testid="fltr-preset-today" onClick={() => applyPreset("today")}
                  className="h-8 px-2.5 text-[11px] border border-[#E5E2D9] rounded hover:bg-[#F7F6F2]">Today</button>
          <button data-testid="fltr-preset-month" onClick={() => applyPreset("month")}
                  className="h-8 px-2.5 text-[11px] border border-[#E5E2D9] rounded hover:bg-[#F7F6F2]">This month</button>
          <button data-testid="fltr-preset-lastmonth" onClick={() => applyPreset("lastmonth")}
                  className="h-8 px-2.5 text-[11px] border border-[#E5E2D9] rounded hover:bg-[#F7F6F2]">Last month</button>
          <input type="date" data-testid="fltr-date-from" value={filters.date_from}
                 onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                 className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded"/>
          <span className="text-[11px] text-[#8A8780]">→</span>
          <input type="date" data-testid="fltr-date-to" value={filters.date_to}
                 onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                 className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded"/>
          {/* Location cascade */}
          <select data-testid="fltr-district" value={filters.district_id}
                  onChange={(e) => setFilters(f => ({ ...f, district_id: e.target.value, lsgd_id: "", ward_id: "" }))}
                  className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white">
            <option value="">All districts</option>
            {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select data-testid="fltr-lsgd" value={filters.lsgd_id} disabled={!filters.district_id}
                  onChange={(e) => setFilters(f => ({ ...f, lsgd_id: e.target.value, ward_id: "" }))}
                  className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white disabled:opacity-50">
            <option value="">All LSGDs</option>
            {lsgds.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select data-testid="fltr-ward" value={filters.ward_id} disabled={!filters.lsgd_id}
                  onChange={(e) => setFilters(f => ({ ...f, ward_id: e.target.value }))}
                  className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white disabled:opacity-50">
            <option value="">All wards</option>
            {wards.map(d => <option key={d.id} value={d.id}>
              {d.ward_no ? `#${d.ward_no} · ${d.name}` : d.name}
            </option>)}
          </select>
          <select data-testid="fltr-status" value={filters.payment_status}
                  onChange={(e) => setFilters(f => ({ ...f, payment_status: e.target.value }))}
                  className="h-8 px-2 text-[11px] border border-[#E5E2D9] rounded bg-white">
            <option value="">All statuses</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="refunded">Refunded</option>
          </select>
          <div className="relative">
            <MagnifyingGlass size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
            <input data-testid="fltr-search" value={filters.search}
                   onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                   placeholder="Invoice / customer / phone"
                   className="h-8 pl-6 pr-2 text-[11px] border border-[#E5E2D9] rounded w-52"/>
          </div>
          <button data-testid="fltr-reset" onClick={resetFilters}
                  className="h-8 px-2.5 text-[11px] text-[#8A8780] hover:text-[#3A3A38]">
            Reset
          </button>
        </div>
      </div>

      {/* Iter 139 — Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-testid="inv-summary-strip">
          <SummaryChip label="Total bills" value={summary.total} testid="sum-total"/>
          <SummaryChip label="Paid" value={summary.paid_count} tone="green" testid="sum-paid-count"/>
          <SummaryChip label="Unpaid" value={summary.unpaid_count} tone="amber" testid="sum-unpaid-count"/>
          <SummaryChip label="Paid ₹" value={`₹${(summary.paid_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`} tone="green" testid="sum-paid-amt"/>
          <SummaryChip label="Unpaid ₹" value={`₹${(summary.unpaid_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`} tone="amber" testid="sum-unpaid-amt"/>
        </div>
      )}

      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                {isSuper && (
                  <th className="pl-6 pr-2 py-3 w-10">
                    <input type="checkbox" data-testid="inv-select-all"
                           checked={items.length > 0 && items.every(i => selected.has(i.id))}
                           onChange={e => setSelected(e.target.checked ? new Set(items.map(i => i.id)) : new Set())}/>
                  </th>
                )}
                <th className="px-6 py-3">Invoice No</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Customer</th>
                <th className="px-6 py-3">Items</th>
                <th className="px-6 py-3 text-right">Subtotal</th>
                <th className="px-6 py-3 text-right">GST</th>
                <th className="px-6 py-3 text-right">Total</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={isSuper ? 10 : 9} className="px-6 py-16 text-center text-[#8A8780]"><ReceiptX size={28} className="inline-block mb-3 opacity-50"/><br/>No invoices yet.</td></tr>}
              {items.map((inv, i) => (
                <tr key={inv.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                  {isSuper && (
                    <td className="pl-6 pr-2 py-3">
                      <input type="checkbox" data-testid={`inv-select-${inv.id}`}
                             checked={selected.has(inv.id)}
                             onChange={e => {
                               const s = new Set(selected);
                               e.target.checked ? s.add(inv.id) : s.delete(inv.id);
                               setSelected(s);
                             }}/>
                    </td>
                  )}
                  <td className="px-6 py-3 font-mono font-medium">{inv.invoice_no}</td>
                  <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">{inv.date}</td>
                  <td className="px-6 py-3">
                    <div className="font-medium">{inv.customer?.name}</div>
                    {inv.customer?.phone && <div className="text-xs text-[#5C5A55]">{inv.customer.phone}</div>}
                  </td>
                  <td className="px-6 py-3 text-xs text-[#5C5A55]">{inv.items?.length || 0} line{inv.items?.length !== 1 ? "s" : ""}</td>
                  <td className="px-6 py-3 font-mono text-right">₹{inv.subtotal?.toFixed(2)}</td>
                  <td className="px-6 py-3 font-mono text-right text-[#5C5A55]">₹{inv.gst_amount?.toFixed(2)} <span className="text-[10px]">({inv.gst_rate}%)</span></td>
                  <td className="px-6 py-3 font-mono text-right font-semibold">₹{inv.total?.toFixed(2)}</td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${STATUS_STYLE[inv.payment_status]}`}>{inv.payment_status}</span>
                    {inv.manual_flag && <span className="ml-1 text-[9px] uppercase tracking-[0.18em] text-[#C25D38]">MANUAL</span>}
                    <InvoicePrepaidCell inv={inv} onApplied={load}/>
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1" data-testid={`inv-actions-${inv.id}`}>
                      {/* Iter 138 — Download menu: choose A4 / A5 / Thermal PDF */}
                      <DownloadMenu inv={inv} onPick={(paper) => pdf(inv, false, paper)}/>
                      <ActionIcon
                        title="Detailed PDF invoice (GPS · QR bags · chain of custody)"
                        testid={`inv-pdf-detailed-${inv.id}`}
                        onClick={() => pdf(inv, true)}
                        color="#0B2B66"
                        bg="hover:bg-[#E4EAF5]"
                        Icon={Database}
                      />
                      <ActionIcon
                        title="Print receipt on Bluetooth thermal printer (58/80mm)"
                        testid={`inv-thermal-${inv.id}`}
                        onClick={() => thermal(inv)}
                        color="#7A2E18"
                        bg="hover:bg-[#F4E6DE]"
                        Icon={Printer}
                      />
                      <ActionIcon
                        title={inv.ebill_url ? "Copy e-bill share link" : "E-bill link not ready"}
                        testid={`inv-ebill-${inv.id}`}
                        disabled={!inv.ebill_url}
                        onClick={() => {
                          if (!inv.ebill_url) return;
                          navigator.clipboard?.writeText(inv.ebill_url);
                          toast.success("e-bill URL copied");
                        }}
                        color="#5C5A55"
                        bg="hover:bg-[#F0EEE8]"
                        Icon={LinkIcon}
                      />
                      <ActionIcon
                        title={
                          inv.customer?.phone
                            ? `Send via WhatsApp API → ${inv.customer.phone}`
                            : "Customer has no phone on file"
                        }
                        testid={`inv-wa-api-${inv.id}`}
                        disabled={!inv.customer?.phone}
                        onClick={() => sendWhatsApp(inv, "api")}
                        color="#25D366"
                        bg="hover:bg-[#DEF7E5]"
                        Icon={WhatsappLogo}
                        iconWeight="fill"
                        badge={inv.whatsapp_count > 0 ? `×${inv.whatsapp_count}` : null}
                      />
                      <ActionIcon
                        title={
                          inv.customer?.phone
                            ? `Open WhatsApp manually with prefilled message`
                            : "Customer has no phone on file"
                        }
                        testid={`inv-wa-manual-${inv.id}`}
                        disabled={!inv.customer?.phone}
                        onClick={() => sendWhatsApp(inv, "manual")}
                        color="#1EAE54"
                        bg="hover:bg-[#DEF7E5]"
                        Icon={WhatsappLogo}
                      />
                      <ActionIcon
                        title={
                          inv.customer?.email
                            ? `Email invoice to ${inv.customer.email}`
                            : "Customer has no email on file"
                        }
                        testid={`inv-email-${inv.id}`}
                        disabled={!inv.customer?.email}
                        onClick={() => sendEmail(inv)}
                        color="#2D5A27"
                        bg="hover:bg-[#E8EFE6]"
                        Icon={Envelope}
                        iconWeight="fill"
                        badge={inv.email_count > 0 ? `×${inv.email_count}` : null}
                      />
                      {inv.payment_status === "unpaid" && (
                        <ActionIcon
                          title="Record manual payment / verify"
                          testid={`inv-pay-${inv.id}`}
                          onClick={() => setOpenPay(inv)}
                          color="#C25D38"
                          bg="hover:bg-[#FBE7DD]"
                          Icon={CreditCard}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {openNew && <InvoiceBuilder initial={prefill}
                                       onClose={() => { setOpenNew(false); setPrefill(null); }}
                                       onSaved={load}/>}
      {openPay && <PaymentModal invoice={openPay} onClose={() => setOpenPay(null)} onDone={load}/>}
      {consolidatedOpen && (
        <ConsolidatedModal
          summary={summary}
          filters={filters}
          onClose={() => setConsolidatedOpen(false)}
          onExport={exportConsolidated}
        />
      )}
      {deleteOpen && (
        <InvoiceBulkDeleteModal
          mode={deleteOpen.mode}
          count={deleteOpen.mode === "all" ? items.length : selected.size}
          onClose={() => setDeleteOpen(false)}
          onConfirm={() => {
            // Iter 178 · gated behind the shared 2-step danger-zone modal.
            setDeleteOpen(false);
            setDzOpen({
              mode: deleteOpen.mode,
              count: deleteOpen.mode === "all" ? items.length : selected.size,
              ids: Array.from(selected),
            });
          }}
        />
      )}
      {dzOpen && (
        <DangerZoneModal
          action="invoices.bulk_delete"
          summary={`Delete ${dzOpen.count.toLocaleString()} invoice${dzOpen.count === 1 ? "" : "s"}`}
          busy={dzBusy}
          onCancel={() => setDzOpen(false)}
          onConfirm={async ({ challenge_id, otp }) => {
            setDzBusy(true);
            try {
              const body = { confirm: "DELETE", challenge_id, otp };
              if (dzOpen.mode === "all") body.all = true;
              else body.ids = dzOpen.ids;
              const r = await api.post("/finance/invoices/bulk-delete", body);
              toast.success(`${r.data.deleted} invoice(s) deleted`);
              setSelected(new Set());
              setDzOpen(false);
              load();
            } catch (e) {
              toast.error(formatApiError(e));
              throw e;
            } finally {
              setDzBusy(false);
            }
          }}
        />
      )}
    </AppShell>
  );
}

function SummaryChip({ label, value, tone = "default", testid }) {
  const bg = tone === "green" ? "bg-[#E8F0E6] text-[#1A3317]"
          : tone === "amber" ? "bg-[#F9E4B7] text-[#7A5A11]"
          : "bg-[#F7F6F2] text-[#3A3A38]";
  return (
    <div data-testid={testid}
         className={`${bg} border border-[#E5E2D9] rounded-md px-3 py-2`}>
      <div className="text-[9px] uppercase tracking-[0.2em] opacity-70">{label}</div>
      <div className="font-heading text-lg font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function ConsolidatedModal({ summary, filters, onClose, onExport }) {
  const [groupBy, setGroupBy] = useState("customer");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onExport(groupBy); } finally { setBusy(false); }
  };
  const hasDate = filters.date_from || filters.date_to;
  const dateLabel = hasDate
    ? `${filters.date_from || "beginning"} → ${filters.date_to || "today"}`
    : "All time (no date filter applied)";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="consolidated-modal">
      <div className="bg-white w-full max-w-md rounded-md p-5 space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Download</div>
            <div className="font-heading text-lg inline-flex items-center gap-1.5">
              <Users size={16}/> Consolidated bills PDF
            </div>
          </div>
          <button onClick={onClose} data-testid="consolidated-close"
                  className="p-1 rounded hover:bg-[#F7F6F2]"><X size={14}/></button>
        </div>

        <div className="text-[11px] bg-[#F7F6F2] rounded-md p-2.5 space-y-1">
          <div><span className="text-[#8A8780]">Period:</span> {dateLabel}</div>
          {summary && (
            <div><span className="text-[#8A8780]">Scope:</span> {summary.total} invoice(s) · ₹{(summary.grand_amount || 0).toLocaleString("en-IN")}</div>
          )}
          <div className="text-[#8A8780] italic">Applies the current filters. Each customer becomes one invoice-styled page.</div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-1.5">Group by</div>
          <div className="flex gap-2">
            {[
              { v: "customer", l: "Customer" },
              { v: "lsgd", l: "LSGD" },
              { v: "ward", l: "Ward" },
            ].map(o => (
              <button key={o.v} onClick={() => setGroupBy(o.v)}
                      data-testid={`consolidated-group-${o.v}`}
                      className={`flex-1 h-10 border rounded text-xs font-medium ${
                        groupBy === o.v
                          ? "border-[#2D5A27] bg-[#EFF5EC] text-[#1A3317]"
                          : "border-[#E5E2D9] hover:bg-[#F7F6F2]"
                      }`}>{o.l}</button>
            ))}
          </div>
        </div>

        <button onClick={go} disabled={busy} data-testid="consolidated-download"
                className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
          <FilePdf size={14}/>{busy ? "Preparing…" : "Download consolidated PDF"}
        </button>
      </div>
    </div>
  );
}

const InvoiceBulkDeleteModal = ({ mode, count, onClose, onConfirm }) => {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = typed === "DELETE";
  const go = async () => {
    if (!ok) return;
    setBusy(true);
    try { await onConfirm(); } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="inv-bulk-delete-modal">
        <div className="w-12 h-12 rounded-full bg-[#F9EDE7] flex items-center justify-center mb-4">
          <Warning size={22} weight="fill" color="#C25D38"/>
        </div>
        <div className="font-heading text-lg mb-2">
          {mode === "all" ? "Delete ALL invoices?" : `Delete ${count} selected invoice(s)?`}
        </div>
        <div className="text-sm text-[#5C5A55] mb-4">
          This action is <b>permanent</b>. Linked payouts and transactions will also be removed.
          <br/>Type <code className="font-mono bg-[#F5F2EC] px-1.5 py-0.5 rounded text-[#C25D38]">DELETE</code> to confirm.
        </div>
        <input value={typed} onChange={e => setTyped(e.target.value)} autoFocus
               data-testid="inv-bulk-delete-confirm"
               placeholder="Type DELETE"
               className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm mb-4 font-mono"/>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={go} disabled={!ok || busy} data-testid="inv-bulk-delete-submit"
                  className="h-10 px-4 bg-[#C25D38] hover:bg-[#A74A29] disabled:opacity-40 text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Trash size={14}/>{busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

function InvoiceBuilder({ onClose, onSaved, initial }) {
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [fieldStaff, setFieldStaff] = useState([]);
  const [wasteTypes, setWasteTypes] = useState([]);
  const [charges, setCharges] = useState([]);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [refFailed, setRefFailed] = useState([]);
  const [refRetry, setRefRetry] = useState(0);

  const [mode, setMode] = useState(initial?.customer_id ? "existing" : "walkin"); // walkin or existing
  const [customerId, setCustomerId] = useState(initial?.customer_id || "");
  const [walkinName, setWalkinName] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [serviceCategoryId, setServiceCategoryId] = useState(initial?.service_category_id || "");
  const [packageId, setPackageId] = useState(initial?.package_id || "");
  const [wasteLines, setWasteLines] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [extraIds, setExtraIds] = useState([]);
  const [distance, setDistance] = useState(0);
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const [serviceDate, setServiceDate] = useState(initial?.service_date || todayIso());
  const [dueDate, setDueDate] = useState(initial?.service_date || todayIso());
  const [fieldStaffId, setFieldStaffId] = useState(initial?.field_staff_id || "");
  const [extraSegments, setExtraSegments] = useState([]); // [{date, field_staff_id, label, amount}]
  // Iter 41 · Manual-invoice QR toggle. When ON, the entered codes get
  // "collected" + chain-of-custody-linked on save.
  const [attachQr, setAttachQr] = useState(false);
  const [qrCodesText, setQrCodesText] = useState("");
  // Iter 238 · duplicate-bill guardrail
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [srcSrId] = useState(initial?.source_sr_id || "");
  const [discType, setDiscType] = useState("");
  const [discValue, setDiscValue] = useState("");
  const [discReason, setDiscReason] = useState("");
  const [srPrepaid, setSrPrepaid] = useState(null);
  const [prepaidOpts, setPrepaidOpts] = useState([]);
  const [prepaidSrId, setPrepaidSrId] = useState("");
  useEffect(() => {
    if (srcSrId) {
      api.get(`/finance/sr-prepaid/${srcSrId}`).then(r => setSrPrepaid(r.data.prepaid)).catch(() => {});
      return;
    }
    setPrepaidOpts([]); setPrepaidSrId(""); setSrPrepaid(null);
    if (!customerId) return;
    api.get(`/finance/customers/${customerId}/prepaid-srs`).then(r => {
      setPrepaidOpts(r.data.items);
      if (r.data.items.length === 1) { setPrepaidSrId(r.data.items[0].sr_id); setSrPrepaid(r.data.items[0]); }
    }).catch(() => {});
  }, [srcSrId, customerId]);
  // Iter 136 — customer type-to-search + package auto-apply signals.
  const [custSearch, setCustSearch] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [custLoading, setCustLoading] = useState(false);
  const [custError, setCustError] = useState("");
  const [custRetry, setCustRetry] = useState(0);
  const mergeCustomers = (rows) => setCustomers(prev => {
    const m = new Map(prev.map(c => [c.id, c]));
    rows.forEach(c => m.set(c.id, { ...m.get(c.id), ...c }));
    return [...m.values()];
  });
  useEffect(() => {
    const q = custSearch.trim();
    setCustError("");
    if (!q) { setFilteredCustomers([]); setCustLoading(false); return; }
    setCustLoading(true);
    let live = true;
    const t = setTimeout(() => {
      api.get("/customers", { params: { status: "approved", q, limit: 20 } })
        .then(r => { if (!live) return; setFilteredCustomers(r.data); mergeCustomers(r.data); })
        .catch(e => { if (live) { setFilteredCustomers([]); setCustError(formatApiError(e)); } })
        .finally(() => { if (live) setCustLoading(false); });
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [custSearch, custRetry]);
  useEffect(() => {
    if (!initial?.customer_id) return;
    api.get(`/customers/${initial.customer_id}`).then(r => mergeCustomers([r.data])).catch(() => {});
  }, [initial?.customer_id]);
  const selectedCustomer = React.useMemo(
    () => customers.find(c => c.id === customerId), [customers, customerId]);
  const customerHasPackage = !!selectedCustomer?.package_id;
  const packageBlink = mode === "existing" && customerHasPackage
                        && packageId === selectedCustomer?.package_id;
  const packageOverridden = mode === "existing" && customerHasPackage
                              && packageId && packageId !== selectedCustomer?.package_id;

  useEffect(() => {
    const sources = [
      ["/services", setServices], ["/waste-types", setWasteTypes],
      ["/charges", setCharges], ["/products", setProducts], ["/packages", setPackages],
      ["/finance/settings", setSettings], ["/service-categories", setServiceCategories],
      ["/staff", setFieldStaff],
    ];
    setRefFailed([]);
    sources.forEach(([url, set]) => {
      api.get(url).then(r => set(r.data || (url === "/finance/settings" ? null : [])))
        .catch(() => setRefFailed(f => (f.includes(url) ? f : [...f, url])));
    });
  }, [refRetry]);

  // Default the package + service category to the customer's mapped values
  useEffect(() => {
    if (mode !== "existing" || !customerId) return;
    const c = customers.find((x) => x.id === customerId);
    if (!c) return;
    if (c.package_id) setPackageId(c.package_id);
    // Auto service-category: customer.preferred_service_category OR derive from package
    let sc = [c.preferred_service_category, c.service_category_id]
      .find(v => v && serviceCategories.some(x => x.id === v)) || "";
    if (!sc && c.package_id) {
      const pkg = packages.find(p => p.id === c.package_id);
      if (pkg?.service_category_id) sc = pkg.service_category_id;
    }
    if (sc) setServiceCategoryId(sc);
    // Auto field staff: customer.assigned_field_staff_id (if exists)
    if (c.assigned_field_staff_id) setFieldStaffId(c.assigned_field_staff_id);
  }, [customerId, customers, packages, serviceCategories, mode]);

  const switchMode = (m) => {
    setMode(m);
    setPackageId("");
    if (m === "walkin") setCustomerId("");
  };
  const addWaste = () => setWasteLines(w => [...w, { waste_type_id: wasteTypes[0]?.id || "", quantity_kg: 1 }]);
  const addProduct = () => setProductLines(p => [...p, { product_id: products[0]?.id || "", quantity: 1 }]);
  const toggleExtra = (id) => setExtraIds(l => l.includes(id) ? l.filter(x => x !== id) : [...l, id]);

  // --- Reusable line calculator for primary + consolidated segments ---
  const computeLines = (input) => {
    const { packageId: pid, wasteLines: wls = [], productLines: pls = [],
             extraIds: exIds = [] } = input;
    const out = { items: [], sub: 0, inclusive: 0, excess_gst_added: 0, excess_gst_embedded: 0 };
    const pkg = packages.find(p => p.id === pid);
    if (pkg) {
      const amt = Number(pkg.price);
      const isIncl = pkg.gst_inclusive !== false;
      out.items.push({
        label: `Package: ${pkg.name}${isIncl ? " (GST incl.)" : ""}`,
        qty: 1, rate: amt, amount: amt,
        line_gst_percent: Number(pkg.gst_percent) || 0,
        line_gst_inclusive: isIncl,
      });
      out.sub += amt;
      if (isIncl) out.inclusive += amt;
    }
    const kgByCategory = {};
    const packageCategories = new Set(
      (pkg?.items || [])
        .filter(pi => pi.kind === "waste" && pi.category)
        .map(pi => pi.category),
    );
    wls.forEach(l => {
      const wt = wasteTypes.find(w => w.id === l.waste_type_id);
      if (!wt) return;
      const qty = Number(l.quantity_kg || 0);
      if (qty <= 0) return;
      const covered = wt.category && packageCategories.has(wt.category);
      if (covered) {
        out.items.push({
          label: `  ↳ ${wt.waste_name} (covered by package)`,
          qty: `${qty}kg`, rate: 0, amount: 0,
          transaction_type: "CHARGE",
        });
      } else {
        const txn = (wt.transaction_type || "CHARGE").toUpperCase();
        const rate = Number(wt.customer_rate ?? wt.rate_per_kg ?? 0);
        const dir = txn === "PAY" ? -1 : 1;
        const amt = dir * rate * qty;
        out.items.push({
          label: `${txn === "PAY" ? "PAY " : ""}${wt.waste_name}`,
          qty: `${qty}kg`, rate: dir > 0 ? rate : -rate,
          amount: amt, transaction_type: txn,
        });
        out.sub += amt;
      }
      if (wt.category) {
        kgByCategory[wt.category] = (kgByCategory[wt.category] || 0) + qty;
      }
    });
    if (pkg && Array.isArray(pkg.items)) {
      pkg.items.forEach(pi => {
        if (pi.kind !== "waste" || !pi.category) return;
        const allowed = Number(pi.allowed_quantity || 0);
        const excessRate = Number(pi.excess_rate || 0);
        const collected = Number((kgByCategory[pi.category] || 0).toFixed(3));
        const overage = Math.max(0, Number((collected - allowed).toFixed(3)));
        out.items.push({
          label: `${pi.category} quota: ${collected} kg of ${allowed} kg allowed${overage > 0 ? ` · ${overage} kg over` : " · within"}`,
          qty: "", rate: 0, amount: 0, transaction_type: "CHARGE",
        });
        if (excessRate > 0 && overage > 0) {
          const amt = Number((overage * excessRate).toFixed(2));
          const piPct = Number(pi.gst_percent) || 0;
          const piIncl = !!pi.gst_inclusive;
          out.items.push({
            label: `Excess: ${pi.category} (${overage} kg × ₹${excessRate}/kg)`,
            qty: `${overage}kg`, rate: excessRate, amount: amt,
            transaction_type: "CHARGE",
            line_gst_percent: piPct,
            line_gst_inclusive: piIncl,
          });
          out.sub += amt;
        }
      });
    }
    pls.forEach(l => {
      const p = products.find(x => x.id === l.product_id);
      if (!p) return;
      const amt = Number(p.price) * Number(l.quantity || 0);
      out.items.push({ label: p.name, qty: l.quantity, rate: p.price, amount: amt });
      out.sub += amt;
    });
    exIds.forEach(id => {
      const e = charges.find(x => x.id === id);
      if (!e) return;
      out.items.push({ label: `Extra: ${e.type}`, qty: 1, rate: e.amount, amount: Number(e.amount) });
      out.sub += Number(e.amount);
    });
    return out;
  };

  // live preview
  const [preview, setPreview] = useState({ subtotal: 0, gst_amount: 0, total: 0, items: [], gst_added: 0, gst_embedded: 0 });
  useEffect(() => {
    if (!settings) return;
    const primary = computeLines({
      packageId, wasteLines, productLines, extraIds,
    });
    let items = [...primary.items];
    extraSegments.forEach(seg => {
      const c = computeLines({
        packageId: seg.package_id,
        wasteLines: seg.waste_lines || [],
        productLines: seg.product_lines || [],
        extraIds: seg.extra_charge_ids || [],
      });
      if (c.items.length > 0 || c.sub !== 0) {
        const staffName = fieldStaff.find(s => s.id === seg.field_staff_id)?.name;
        const header = staffName
          ? `━ ${seg.service_date} · by ${staffName}`
          : `━ ${seg.service_date}`;
        items.push({
          label: header, qty: "", rate: 0, amount: 0,
          is_segment_header: true, transaction_type: "CHARGE",
        });
        c.items.forEach(it => items.push({ ...it, label: `  ${it.label}` }));
      }
    });
    // Per-line GST aggregation — mirror backend _aggregate_gst.
    const defaultRate = Number(settings.gst_rate) || 0;
    let sub = 0;
    let gstAdded = 0;
    let gstEmbedded = 0;
    items.forEach(it => {
      if (it.is_segment_header) return;
      const amt = Number(it.amount || 0);
      if (amt === 0) return;
      sub += amt;
      const hasMeta = it.line_gst_percent !== undefined;
      const pct = hasMeta ? Number(it.line_gst_percent) || 0 : defaultRate;
      const incl = hasMeta ? !!it.line_gst_inclusive : false;
      if (pct <= 0) return;
      if (incl) {
        gstEmbedded += amt - (amt * 100) / (100 + pct);
      } else {
        gstAdded += (amt * pct) / 100;
      }
    });
    // Iter 257 · discount before GST — scales taxable base (and its GST) proportionally
    const positive = items.reduce((a, it) => a + (!it.is_segment_header && Number(it.amount) > 0 ? Number(it.amount) : 0), 0);
    const dv = Number(discValue) || 0;
    const discount = discType && dv > 0 && positive > 0
      ? Math.min(discType === "percent" ? (positive * Math.min(dv, 100)) / 100 : dv, positive) : 0;
    const f = positive > 0 ? 1 - discount / positive : 1;
    setPreview({
      items,
      discount,
      subtotal: sub - discount,
      gst_amount: (gstAdded + gstEmbedded) * f,
      gst_added: gstAdded * f,
      gst_embedded: gstEmbedded * f,
      total: sub - discount + gstAdded * f,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId, wasteLines, productLines, extraIds, discType, discValue,
       extraSegments,
       packages, wasteTypes, products, charges, settings, fieldStaff]);

  // Iter 238 · Duplicate guardrail — split submit into two phases so we
  // can flash the NearbyInvoicesModal between form validation and API POST.
  const performSave = async () => {
    try {
      const r = await api.post("/finance/invoices", {
        customer_id: mode === "existing" ? customerId : null,
        walkin_name: mode === "walkin" ? walkinName : null,
        service_category_id: serviceCategoryId || null,
        package_id: packageId || null,
        waste_lines: wasteLines
          .map(l => ({ waste_type_id: l.waste_type_id, quantity_kg: Number(l.quantity_kg) }))
          .filter(l => l.quantity_kg > 0 && l.waste_type_id),
        product_lines: productLines.map(l => ({ product_id: l.product_id, quantity: Number(l.quantity) })),
        extra_charge_ids: extraIds,
        distance_km: Number(distance),
        service_date: serviceDate,
        due_date: dueDate,
        field_staff_id: fieldStaffId || null,
        source_sr_id: srcSrId || null,
        prepaid_sr_id: !srcSrId && prepaidSrId ? prepaidSrId : null,
        discount_type: discType || null,
        discount_value: Number(discValue) || 0,
        discount_reason: discReason.trim() || null,
        consolidated_segments: extraSegments
          .filter(s => s.service_date && (
            (s.package_id || s.waste_lines?.length || s.product_lines?.length || s.extra_charge_ids?.length)
          ))
          .map(s => ({
            service_date: s.service_date,
            field_staff_id: s.field_staff_id || null,
            package_id: s.package_id || null,
            waste_lines: (s.waste_lines || [])
              .filter(l => l.waste_type_id && Number(l.quantity_kg) > 0)
              .map(l => ({ waste_type_id: l.waste_type_id, quantity_kg: Number(l.quantity_kg) })),
            product_lines: (s.product_lines || [])
              .filter(l => l.product_id && Number(l.quantity) > 0)
              .map(l => ({ product_id: l.product_id, quantity: Number(l.quantity) })),
            extra_charge_ids: s.extra_charge_ids || [],
            distance_km: Number(s.distance_km || 0),
          })),
        attach_qr_bags: attachQr
          ? qrCodesText.split(/[\s,]+/).map(x => x.trim().toUpperCase()).filter(Boolean)
          : [],
      });
      toast.success(`Invoice ${r.data.invoice_no} created`);
      onSaved?.(); onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (discType && Number(discValue) > 0 && !discReason.trim()) {
      toast.error("Enter a reason for the discount"); return;
    }
    if (discType === "percent" && Number(discValue) > 100) {
      toast.error("Discount % cannot exceed 100"); return;
    }
    // For "existing customer" mode, run the duplicate-bill check before
    // POSTing. Walk-in bills bypass the guard.
    if (mode === "existing" && customerId && serviceDate) {
      setPendingSubmit({ customer_id: customerId, service_date: serviceDate });
      return;
    }
    await performSave();
  };

  // When a package is selected, auto-populate waste lines grouped by category.
  // Each waste_type under every category listed in the package shows up as a
  // row with quantity defaulting to 0.
  React.useEffect(() => {
    // Purge any previously package-seeded waste lines; new ones will be added below.
    setWasteLines(curr => curr.filter(l => !l._from_package));
    if (!packageId) return;
    const pkg = packages.find(p => p.id === packageId);
    if (!pkg) return;
    const wasteCategoryItems = (pkg.items || [])
      .filter(it => it.kind === "waste" && it.category);
    if (wasteCategoryItems.length === 0) return;
    const seeded = [];
    wasteCategoryItems.forEach(pkgItem => {
      const typesInCategory = wasteTypes
        .filter(w => w.category === pkgItem.category)
        .sort((a, b) => a.waste_name.localeCompare(b.waste_name));
      typesInCategory.forEach(w => {
        seeded.push({ waste_type_id: w.id, quantity_kg: 0, _from_package: pkgItem.category });
      });
    });
    if (seeded.length) setWasteLines(curr => [...curr, ...seeded]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId, packages, wasteTypes]);

  const wasteLinesGrouped = React.useMemo(() => {
    const groups = {};
    const ungrouped = [];
    wasteLines.forEach((l, idx) => {
      const row = { ...l, _idx: idx };
      if (l._from_package) {
        (groups[l._from_package] = groups[l._from_package] || []).push(row);
      } else {
        ungrouped.push(row);
      }
    });
    return { groups, ungrouped };
  }, [wasteLines]);

  const setLineQty = (idx, v) =>
    setWasteLines(w => w.map((x, i) => i === idx ? { ...x, quantity_kg: v } : x));
  const removeLine = (idx) =>
    setWasteLines(w => w.filter((_, i) => i !== idx));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      {pendingSubmit && (
        <NearbyInvoicesModal
          customerId={pendingSubmit.customer_id}
          serviceDate={pendingSubmit.service_date}
          windowDays={2}
          onCancel={() => setPendingSubmit(null)}
          onProceed={async () => { setPendingSubmit(null); await performSave(); }}
        />
      )}
      <div className="bg-white rounded-md w-full max-w-4xl max-h-[92vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E5E2D9] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Finance</div>
            <div className="font-heading text-2xl">Manual invoice builder</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={18}/></button>
        </div>
        <div className="grid grid-cols-3 gap-6 p-6">
          <form onSubmit={submit} className="col-span-2 space-y-5" data-testid="invoice-form">
            <div className="flex gap-2">
              <button type="button" onClick={() => switchMode("walkin")}
                      data-testid="mode-walkin"
                      className={`flex-1 h-10 rounded-md text-sm border ${mode === "walkin" ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>Walk-in</button>
              <button type="button" onClick={() => switchMode("existing")}
                      data-testid="mode-existing"
                      className={`flex-1 h-10 rounded-md text-sm border ${mode === "existing" ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>Existing customer</button>
            </div>
            {mode === "walkin" ? (
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Walk-in name</label>
                <input data-testid="inv-walkin-name" required value={walkinName} onChange={e => setWalkinName(e.target.value)}
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
              </div>
            ) : (
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Customer</label>
                <input
                  type="text"
                  value={custSearch}
                  onChange={e => setCustSearch(e.target.value)}
                  data-testid="inv-customer-search"
                  placeholder="Type name, phone, customer ID or code…"
                  className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm mb-2"/>
                {customerId ? (
                  <div className="flex items-center gap-2 h-10 px-3 border border-[#2D5A27] rounded-md bg-[#EFF3EE]"
                       data-testid="inv-customer-picked">
                    <span className="text-sm font-medium truncate">
                      {selectedCustomer?.name || "—"}
                      <span className="text-[10px] text-[#5C5A55] ml-2 font-mono">
                        {selectedCustomer?.customer_code} · {selectedCustomer?.phone}
                      </span>
                    </span>
                    <button type="button"
                            onClick={() => { setCustomerId(""); setCustSearch(""); }}
                            data-testid="inv-customer-clear"
                            className="ml-auto text-[11px] text-[#7A2E18] hover:underline">
                      Change
                    </button>
                  </div>
                ) : custSearch ? (
                  <div className="border border-[#E5E2D9] rounded-md max-h-60 overflow-y-auto bg-white"
                       data-testid="inv-customer-suggestions">
                    {custLoading && (
                      <div className="p-3 text-[11px] text-[#5C5A55] italic" data-testid="inv-customer-loading">Searching…</div>
                    )}
                    {!custLoading && custError && (
                      <div className="p-3 text-[11px] text-[#7A2E18] flex items-center gap-2" data-testid="inv-customer-error">
                        <span className="flex-1">Couldn't load customers: {custError}</span>
                        <button type="button" onClick={() => setCustRetry(n => n + 1)}
                                data-testid="inv-customer-retry"
                                className="px-2 py-1 border border-[#7A2E18] rounded text-[11px] font-medium hover:bg-[#FBEDE8]">Retry</button>
                      </div>
                    )}
                    {!custLoading && !custError && filteredCustomers.length === 0 && (
                      <div className="p-3 text-[11px] text-[#8A8780] italic">No customers match.</div>
                    )}
                    {filteredCustomers.map(c => (
                      <button key={c.id} type="button"
                              data-testid={`inv-customer-opt-${c.id}`}
                              onClick={() => { setCustomerId(c.id); setCustSearch(""); }}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-[#F7F6F2] border-b border-[#F7F6F2] flex items-center gap-2">
                        <span className="font-medium truncate flex-1">{c.name}</span>
                        <span className="font-mono text-[10px] text-[#5C5A55]">{c.customer_code}</span>
                        <span className="text-[10px] text-[#8A8780]">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-[10px] text-[#8A8780] italic">
                    Start typing a name, phone or customer code to search.
                  </div>
                )}
                {refFailed.length > 0 && (
                  <div className="mt-2 p-2 text-[11px] text-[#7A2E18] border border-[#E8C4B6] bg-[#FBEDE8] rounded-md flex items-center gap-2"
                       data-testid="inv-ref-error">
                    <span className="flex-1">Some invoice data didn't load ({refFailed.join(", ")}). Check the connection.</span>
                    <button type="button" onClick={() => setRefRetry(n => n + 1)} data-testid="inv-ref-retry"
                            className="px-2 py-1 border border-[#7A2E18] rounded font-medium">Retry</button>
                  </div>
                )}
                {/* Hidden native select to preserve `required` validation */}
                <select value={customerId} onChange={() => {}} required
                        data-testid="inv-customer" className="hidden">
                  <option value="">Select</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2 flex items-center gap-2">
                Service Category
                {mode === "existing" && customerId && serviceCategoryId && (
                  <span className="text-[10px] text-[#2D5A27] normal-case tracking-normal font-medium">· auto from customer / package</span>
                )}
              </label>
              <select value={serviceCategoryId} onChange={e => setServiceCategoryId(e.target.value)}
                      data-testid="inv-service-category"
                      className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                <option value="">— none —</option>
                {serviceCategories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.kind ? ` · ${c.kind}` : ""}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2 flex items-center gap-2">
                Package
                {packageBlink && (
                  <span data-testid="inv-package-auto-chip"
                        className="text-[10px] text-[#2D5A27] normal-case tracking-normal font-medium">
                    · auto-applied from customer
                  </span>
                )}
                {packageOverridden && (
                  <span data-testid="inv-package-override-chip"
                        className="text-[10px] text-[#7A2E18] normal-case tracking-normal font-medium">
                    · overriding customer&apos;s default plan
                  </span>
                )}
              </label>
              <select value={packageId} onChange={e => setPackageId(e.target.value)}
                      data-testid="inv-package"
                      className={`w-full h-10 px-3 border rounded-md bg-white transition-colors ${
                        packageBlink ? "border-[#2D5A27] bg-[#EAF5E7] animate-[pkgBlink_1.4s_ease-in-out_2]"
                        : packageOverridden ? "border-[#C25D38]"
                        : "border-[#E5E2D9]"}`}>
                <option value="">— no package —</option>
                {packages.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} · ₹{Number(p.price).toFixed(0)}{p.gst_percent > 0 ? ` · ${p.gst_percent}% ${p.gst_inclusive ? "incl" : "excl"}` : ""}
                    {selectedCustomer?.package_id === p.id ? "  ★ customer default" : ""}
                  </option>
                ))}
              </select>
              {/* Iter 136 — messaging: no package on customer, or admin overriding it. */}
              {mode === "existing" && customerId && !customerHasPackage && (
                <div className="mt-1 text-[11px] text-[#7A5A11] bg-[#FFF8E1] border border-[#F5C518] rounded px-2 py-1"
                     data-testid="inv-package-none-msg">
                  This customer has no default package assigned to their profile.
                  Pick one manually if applicable.
                </div>
              )}
              {packageOverridden && (
                <div className="mt-1 text-[11px] text-[#7A2E18] bg-[#FDECE5] border border-[#C25D38] rounded px-2 py-1"
                     data-testid="inv-package-override-msg">
                  You are choosing a different plan than the customer&apos;s default
                  (<b>{packages.find(p => p.id === selectedCustomer?.package_id)?.name || "—"}</b>).
                </div>
              )}
            </div>

            {/* Service date · Due date · Field staff */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Service Date</label>
                <input type="date" value={serviceDate}
                        onChange={e => setServiceDate(e.target.value)}
                        max={todayIso()}
                        data-testid="inv-service-date"
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm"/>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Payment Due Date</label>
                <input type="date" value={dueDate}
                        onChange={e => setDueDate(e.target.value)}
                        data-testid="inv-due-date"
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm"/>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Field Staff</label>
                <select value={fieldStaffId}
                        onChange={e => setFieldStaffId(e.target.value)}
                        data-testid="inv-field-staff"
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                  <option value="">— unassigned —</option>
                  {fieldStaff.map(s => (
                    <option key={s.id} value={s.id}>{s.name}{s.phone ? ` · ${s.phone}` : ""}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="border border-[#E5E2D9] rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">Waste items</div>
                <button type="button" onClick={addWaste} data-testid="inv-add-waste"
                        className="h-8 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1"><Plus size={10}/>Add</button>
              </div>

              {/* Grouped by package category */}
              {Object.entries(wasteLinesGrouped.groups).map(([cat, rows]) => (
                <div key={cat} className="mb-4" data-testid={`inv-waste-cat-${cat}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-[#D4E0D1] text-[#1A3317]">
                      From package · {cat}
                    </div>
                    <div className="text-[10px] text-[#8A8780]">
                      {rows.length} waste type{rows.length === 1 ? "" : "s"} · enter quantity for items collected
                    </div>
                  </div>
                  <div className="space-y-1">
                    {rows.map((l) => {
                      const wt = wasteTypes.find(w => w.id === l.waste_type_id);
                      return (
                        <div key={l._idx} className="flex items-center gap-2 text-sm" data-testid={`inv-waste-row-${l._idx}`}>
                          <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-[#FAFAF7] border border-[#E5E2D9] rounded-md">
                            <span className="truncate">{wt?.waste_name || "—"}</span>
                            {wt?.transaction_type === "PAY" && (
                              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-[#C25D38]/15 text-[#7A2E18]">pay</span>
                            )}
                          </div>
                          <input type="number" step="any" min="0" value={l.quantity_kg}
                                 data-testid={`inv-waste-qty-${l._idx}`}
                                 onChange={e => setLineQty(l._idx, e.target.value)}
                                 className="w-24 h-9 px-2 border border-[#E5E2D9] rounded-md font-mono text-sm text-right"
                                 placeholder="kg" />
                          <span className="text-[11px] text-[#8A8780] w-4">kg</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Manually-added (non-package) lines */}
              {wasteLinesGrouped.ungrouped.length > 0 && (
                <div className="mb-1">
                  {Object.keys(wasteLinesGrouped.groups).length > 0 && (
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] mb-2">Ad-hoc items</div>
                  )}
                  {wasteLinesGrouped.ungrouped.map((l) => (
                    <div key={l._idx} className="flex gap-2 mb-2">
                      <select value={l.waste_type_id}
                              data-testid={`inv-waste-type-${l._idx}`}
                              onChange={e => setWasteLines(w => w.map((x, idx) => idx === l._idx ? { ...x, waste_type_id: e.target.value } : x))}
                              className="flex-1 h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-sm">
                        {[...wasteTypes].sort((a, b) => a.waste_name.localeCompare(b.waste_name)).map(w => (
                          <option key={w.id} value={w.id}>{w.waste_name}{w.category ? ` (${w.category})` : ""}</option>
                        ))}
                      </select>
                      <input type="number" step="any" placeholder="kg" value={l.quantity_kg}
                             onChange={e => setLineQty(l._idx, e.target.value)}
                             className="w-24 h-9 px-2 border border-[#E5E2D9] rounded-md font-mono text-sm"/>
                      <button type="button" onClick={() => removeLine(l._idx)}
                              className="h-9 px-2 border border-[#E5E2D9] rounded-md text-[#C25D38]"><X size={12}/></button>
                    </div>
                  ))}
                </div>
              )}

              {wasteLines.length === 0 && <div className="text-xs text-[#8A8780]">None.</div>}
            </div>

            <div className="border border-[#E5E2D9] rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">Products</div>
                <button type="button" onClick={addProduct} className="h-8 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1"><Plus size={10}/>Add</button>
              </div>
              {productLines.map((l, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <select value={l.product_id}
                          onChange={e => setProductLines(p => p.map((x, idx) => idx === i ? { ...x, product_id: e.target.value } : x))}
                          className="flex-1 h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-sm">
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input type="number" placeholder="qty" value={l.quantity}
                         onChange={e => setProductLines(p => p.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x))}
                         className="w-20 h-9 px-2 border border-[#E5E2D9] rounded-md font-mono text-sm"/>
                  <button type="button" onClick={() => setProductLines(p => p.filter((_, idx) => idx !== i))}
                          className="h-9 px-2 border border-[#E5E2D9] rounded-md text-[#C25D38]"><X size={12}/></button>
                </div>
              ))}
              {productLines.length === 0 && <div className="text-xs text-[#8A8780]">None.</div>}
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Extra charges</label>
              <div className="flex flex-wrap gap-2">
                {charges.map(c => {
                  const on = extraIds.includes(c.id);
                  return (
                    <button type="button" key={c.id} onClick={() => toggleExtra(c.id)}
                            className={`px-3 h-9 rounded-md text-xs border ${on ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "border-[#E5E2D9] text-[#5C5A55]"}`}>
                      {c.type} · ₹{c.amount}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Consolidate Multiple Bills — full sub-invoice editor per date */}
            <div className="border-2 border-[#2D5A27]/30 rounded-md p-4 bg-[#F2F7F0]"
                  data-testid="inv-consolidated-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#2D5A27] font-semibold">Consolidate Multiple Bills</div>
                  <div className="text-[11px] text-[#5C5A55] mt-0.5">Add charges from other service dates as full sub-invoices — each with its own package, waste, products & extras.</div>
                </div>
                <button type="button"
                        onClick={() => {
                          const c = customers.find(x => x.id === customerId);
                          setExtraSegments(s => [
                            ...s,
                            {
                              service_date: todayIso(),
                              field_staff_id: fieldStaffId || c?.assigned_field_staff_id || "",
                              package_id: c?.package_id || "",
                              waste_lines: [], product_lines: [], extra_charge_ids: [],
                              distance_km: 0,
                            },
                          ]);
                        }}
                        data-testid="inv-add-segment"
                        className="h-9 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-xs font-medium inline-flex items-center gap-1">
                  <Plus size={10} weight="bold"/>Add Another Date's Bill
                </button>
              </div>
              {extraSegments.length === 0 ? (
                <div className="text-[11px] italic text-[#8A8780] py-2">
                  No additional dates yet. Click <b>Add Another Date's Bill</b> to consolidate a previous service date into this single invoice.
                </div>
              ) : (
                <div className="space-y-4">
                  {extraSegments.map((seg, i) => (
                    <SegmentEditor
                      key={i} idx={i} seg={seg}
                      packages={packages} wasteTypes={wasteTypes}
                      products={products} charges={charges} fieldStaff={fieldStaff}
                      onChange={(patch) => setExtraSegments(s =>
                        s.map((x, j) => j === i ? { ...x, ...patch } : x))}
                      onRemove={() => setExtraSegments(s => s.filter((_, j) => j !== i))}
                      todayIso={todayIso}/>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-[#E5E2D9] rounded-md p-3 bg-white" data-testid="inv-qr-block">
              <label className="flex items-center gap-2 text-xs text-[#1A1A1A] cursor-pointer">
                <input type="checkbox" checked={attachQr} onChange={e => setAttachQr(e.target.checked)}
                        data-testid="inv-attach-qr-toggle" className="accent-[#2D5A27] w-4 h-4"/>
                Attach QR bags to this invoice
              </label>
              {attachQr && (
                <div className="mt-2.5">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">
                    QR bag codes (comma / newline separated)
                  </label>
                  <textarea data-testid="inv-attach-qr-codes"
                             value={qrCodesText} onChange={e => setQrCodesText(e.target.value)}
                             placeholder="RSG-ABC-0001, RSG-ABC-0002"
                             rows={3}
                             className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-xs font-mono"/>
                  <div className="text-[10px] text-[#8A8780] mt-1">
                    Each unused bag will be marked <em>collected</em>, linked to this invoice, and added to the chain of custody.
                  </div>
                </div>
              )}
            </div>

            <button data-testid="inv-save" type="submit"
                    className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
              Create invoice
            </button>
          </form>

          <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded-md p-5 h-max sticky top-24">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Preview</div>
            <div className="font-heading text-xl mb-3">Live total</div>
            {preview.items.length === 0 && <div className="text-xs text-[#8A8780] py-3">Add items…</div>}

            {/* Customer Charges */}
            {preview.items.some((it) => it.transaction_type !== "PAY") && (
              <div className="bg-white border border-[#D4E0D1] rounded-md p-3 mb-3" data-testid="pv-charges">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-2">
                  <span>Customer charges</span>
                  <span className="font-mono text-[#1A3317] normal-case tracking-normal">
                    ₹{preview.items.filter((it) => it.transaction_type !== "PAY")
                        .reduce((s, it) => s + Number(it.amount || 0), 0).toFixed(2)}
                  </span>
                </div>
                {preview.items.filter((it) => it.transaction_type !== "PAY").map((it, i) => (
                  <div key={`c${i}`} className="flex justify-between text-sm py-0.5">
                    <span className="text-[#5C5A55] truncate pr-2">{it.label}</span>
                    <span className="font-mono">₹{Number(it.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Customer Payment (PAY lines — we owe the customer) */}
            {preview.items.some((it) => it.transaction_type === "PAY") && (
              <div className="bg-white border border-[#F4C7B9] rounded-md p-3 mb-3" data-testid="pv-payment">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#7A2E18] mb-2">
                  <span>Customer payment</span>
                  <span className="font-mono text-[#7A2E18] normal-case tracking-normal">
                    ₹{preview.items.filter((it) => it.transaction_type === "PAY")
                        .reduce((s, it) => s + Math.abs(Number(it.amount || 0)), 0).toFixed(2)}
                  </span>
                </div>
                {preview.items.filter((it) => it.transaction_type === "PAY").map((it, i) => (
                  <div key={`p${i}`} className="flex justify-between text-sm py-0.5">
                    <span className="text-[#5C5A55] truncate pr-2">{it.label.replace(/^PAY\s+/, "")}</span>
                    <span className="font-mono text-[#7A2E18]">−₹{Math.abs(Number(it.amount || 0)).toFixed(2)}</span>
                  </div>
                ))}
                <div className="text-[10px] text-[#8A8780] mt-1">We pay customer for these items</div>
              </div>
            )}

            <div className="border-t border-[#E5E2D9] mt-3 pt-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#5C5A55]">Net subtotal</span><span className="font-mono">₹{preview.subtotal.toFixed(2)}</span></div>
              {preview.gst_embedded > 0 && (
                <div className="flex justify-between text-xs text-[#8A8780]">
                  <span>↳ GST already included</span>
                  <span className="font-mono">₹{preview.gst_embedded.toFixed(2)}</span>
                </div>
              )}
              {preview.gst_added > 0 && (
                <div className="flex justify-between text-xs text-[#5C5A55]">
                  <span>+ GST on excess / extras</span>
                  <span className="font-mono">₹{preview.gst_added.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between" data-testid="preview-gst-line">
                <span className="text-[#5C5A55]">GST total</span>
                <span className="font-mono">₹{preview.gst_amount.toFixed(2)}</span>
              </div>
              <div className={`flex justify-between font-heading text-lg pt-1 ${preview.total < 0 ? "text-[#7A2E18]" : "text-[#2D5A27]"}`}>
                <span>{preview.total < 0 ? "Pay customer" : srPrepaid ? "Invoice total" : "Total due"}</span>
                <span data-testid="preview-total">₹{Math.abs(preview.total).toFixed(2)}</span>
              </div>
            </div>
            <DiscountBox type={discType} setType={setDiscType} value={discValue} setValue={setDiscValue}
                         reason={discReason} setReason={setDiscReason} discount={preview.discount}/>
            {!srcSrId && prepaidOpts.length > 0 && (
              <div className="mt-3" data-testid="manual-prepaid-picker">
                <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] font-semibold">Apply customer's prepaid amount</label>
                <select value={prepaidSrId} data-testid="manual-prepaid-select"
                        onChange={e => { setPrepaidSrId(e.target.value); setSrPrepaid(prepaidOpts.find(o => o.sr_id === e.target.value) || null); }}
                        className="mt-1 h-9 w-full px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
                  <option value="">Don't apply</option>
                  {prepaidOpts.map(o => <option key={o.sr_id} value={o.sr_id}>{o.request_no} · ₹{o.amount} · {(o.paid_at || "").slice(0, 10)}</option>)}
                </select>
              </div>
            )}
            <PrepaidSettlement prepaid={srPrepaid} total={preview.total} testid="manual-prepaid"/>
            <div className="mt-4 pt-4 border-t border-[#E5E2D9] text-[11px] text-[#5C5A55] leading-relaxed">
              No manual price editing — all rates pulled from pricing engine. Only quantities/lines are adjustable. PAY-type waste reduces the bill total.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InvoicePrepaidCell({ inv, onApplied }) {
  const [opts, setOpts] = useState(null);
  const pre = Number(inv.prepaid_amount || 0);
  if (pre > 0) {
    const due = inv.payment_status === "paid" ? 0 : Number(inv.balance_due ?? 0);
    return (
      <div className="mt-1 text-[10px] leading-tight" data-testid={`inv-prepaid-${inv.id}`}>
        <div className="text-[#2D5A27] font-semibold">Prepaid ₹{pre.toFixed(2)}</div>
        <div className="text-[#8A8780] font-mono">{(inv.prepaid_paid_at || "").slice(0, 10)} · {inv.prepaid_payment_id || "—"}</div>
        <div className={due > 0 ? "text-[#B42828] font-semibold" : "text-[#2D5A27]"} data-testid={`inv-balance-${inv.id}`}>
          {due > 0 ? `Balance due ₹${due.toFixed(2)}` : "Fully paid"}
        </div>
      </div>
    );
  }
  if (inv.payment_status !== "unpaid" || !inv.customer_id) return null;
  const check = async () => {
    try {
      const r = await api.get(`/finance/customers/${inv.customer_id}/prepaid-srs`);
      if (!r.data.items.length) { toast.info("No unused prepaid amount for this customer"); return; }
      setOpts(r.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const apply = async (sr) => {
    try {
      await api.post(`/finance/invoices/${inv.id}/apply-prepaid`, { sr_id: sr.sr_id });
      toast.success(`Applied ₹${sr.amount} prepaid from ${sr.request_no}`);
      setOpts(null); onApplied();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-1">
      {!opts ? (
        <button onClick={check} data-testid={`inv-apply-prepaid-${inv.id}`}
                className="text-[10px] text-[#2D5A27] underline underline-offset-2">Apply prepaid</button>
      ) : opts.map(o => (
        <button key={o.sr_id} onClick={() => apply(o)} data-testid={`inv-apply-prepaid-${inv.id}-${o.request_no}`}
                className="block text-[10px] text-left text-[#2D5A27] hover:underline">
          Use {o.request_no} · ₹{o.amount} ({(o.paid_at || "").slice(0, 10)})
        </button>
      ))}
    </div>
  );
}

function DiscountBox({ type, setType, value, setValue, reason, setReason, discount }) {
  const inp = "h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white";
  return (
    <div className="mt-3 p-3 rounded-md border border-dashed border-[#D9D5C6] space-y-2" data-testid="manual-discount">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] font-semibold">Discount (before GST)</span>
        {discount > 0 && <span className="font-mono text-sm text-[#7A2E18]" data-testid="manual-discount-amount">− ₹{discount.toFixed(2)}</span>}
      </div>
      <div className="flex gap-2">
        <select value={type} onChange={e => setType(e.target.value)} className={inp} data-testid="manual-discount-type">
          <option value="">No discount</option>
          <option value="amount">₹ Amount</option>
          <option value="percent">% Percent</option>
        </select>
        {type && <input type="number" min="0" step="0.01" value={value} onChange={e => setValue(e.target.value)}
                        placeholder={type === "percent" ? "e.g. 10" : "e.g. 50"} className={`${inp} w-28`}
                        data-testid="manual-discount-value"/>}
      </div>
      {type && <input value={reason} onChange={e => setReason(e.target.value)} maxLength={200}
                      placeholder="Reason (required)" className={`${inp} w-full`} data-testid="manual-discount-reason"/>}
    </div>
  );
}

function PaymentModal({ invoice, onClose, onDone }) {
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("gateway");  // gateway | manual
  // Manual fields
  const [mMethod, setMMethod] = useState("cash");
  const [mTxnNo, setMTxnNo] = useState("");
  const [mTxnDate, setMTxnDate] = useState(new Date().toISOString().slice(0, 10));
  const [mStaff, setMStaff] = useState("");
  const [mAmount, setMAmount] = useState("");
  const [mNotes, setMNotes] = useState("");
  const [mBusy, setMBusy] = useState(false);

  useEffect(() => {
    api.get("/finance/providers").then(r => {
      const enabled = r.data.filter(p => p.enabled);
      setProviders(enabled);
      const def = enabled.find(p => p.default) || enabled[0];
      if (def) setProvider(def.key);
    });
  }, []);

  const createOrder = async () => {
    setLoading(true);
    try {
      const r = await api.post("/finance/orders", { invoice_id: invoice.id, provider });
      setOrder(r.data);
      toast.success(`Order created via ${provider.toUpperCase()}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const confirmDemo = async () => {
    try {
      await api.post(`/finance/orders/${order.transaction.id}/confirm-demo`);
      toast.success("Payment confirmed (demo)");
      onDone?.(); onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const chargeItems = (invoice.items || []).filter((it) => it.transaction_type !== "PAY");
  const payItems = (invoice.items || []).filter((it) => it.transaction_type === "PAY");
  const chargeTotal = chargeItems.reduce((s, it) => s + Number(it.amount || 0), 0);
  const payTotal = payItems.reduce((s, it) => s + Math.abs(Number(it.amount || 0)), 0);
  const netDue = Number(invoice.total || 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg p-6 max-h-[92vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Payment</div>
            <div className="font-heading text-xl">{invoice.invoice_no}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>

        {/* Two-section breakdown when both exist */}
        {(chargeItems.length > 0 || payItems.length > 0) && (
          <div className="space-y-3 mb-4">
            {chargeItems.length > 0 && (
              <div className="border border-[#D4E0D1] rounded-md p-3" data-testid="pm-charges">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-2">
                  <span>Customer charges</span>
                  <span className="font-mono text-[#1A3317] normal-case tracking-normal">₹{chargeTotal.toFixed(2)}</span>
                </div>
                {chargeItems.map((it, i) => (
                  <div key={i} className="flex justify-between text-xs py-0.5">
                    <span className="text-[#5C5A55] truncate pr-2">{it.label}</span>
                    <span className="font-mono">₹{Number(it.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
            {payItems.length > 0 && (
              <div className="border border-[#F4C7B9] rounded-md p-3" data-testid="pm-payment">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#7A2E18] mb-2">
                  <span>Customer payment</span>
                  <span className="font-mono text-[#7A2E18] normal-case tracking-normal">₹{payTotal.toFixed(2)}</span>
                </div>
                {payItems.map((it, i) => (
                  <div key={i} className="flex justify-between text-xs py-0.5">
                    <span className="text-[#5C5A55] truncate pr-2">{it.label.replace(/^PAY\s+/, "")}</span>
                    <span className="font-mono text-[#7A2E18]">−₹{Math.abs(Number(it.amount || 0)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="p-4 bg-[#F7F6F2] rounded-md mb-5 text-center">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">
            {netDue < 0 ? "Amount payable to customer" : "Amount due"}
          </div>
          <div className={`font-heading text-4xl ${netDue < 0 ? "text-[#7A2E18]" : "text-[#2D5A27]"}`}>
            ₹{Math.abs(netDue).toFixed(2)}
          </div>
          <div className="text-xs text-[#5C5A55]">{invoice.customer?.name}</div>
          {invoice.created_by_name && (
            <div className="text-[10px] text-[#8A8780] mt-1">Billed by {invoice.created_by_name}</div>
          )}
          {invoice.ebill_url && (
            <div className="text-[10px] text-[#2D5A27] mt-1 font-mono break-all">ebill: {invoice.ebill_url}</div>
          )}
        </div>
        <div className="flex gap-0 mb-4 border border-[#E5E2D9] rounded-md overflow-hidden" data-testid="pm-tabs">
          <button onClick={() => setTab("gateway")} data-testid="pm-tab-gateway"
                  className={`flex-1 h-10 text-xs font-medium ${tab === "gateway" ? "bg-[#2D5A27] text-white" : "bg-white text-[#5C5A55]"}`}>
            Online Gateway
          </button>
          <button onClick={() => setTab("manual")} data-testid="pm-tab-manual"
                  className={`flex-1 h-10 text-xs font-medium border-l border-[#E5E2D9] ${tab === "manual" ? "bg-[#2D5A27] text-white" : "bg-white text-[#5C5A55]"}`}>
            Manual Verify (Cash / UPI / Cheque)
          </button>
        </div>

        {tab === "gateway" && !order && (
          <>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Select provider</div>
            <div className="grid grid-cols-3 gap-2 mb-4" data-testid="provider-grid">
              {providers.map(p => (
                <button key={p.key} onClick={() => setProvider(p.key)} data-testid={`provider-${p.key}`}
                        className={`h-14 rounded-md border-2 text-sm font-medium transition-colors ${provider === p.key ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>
                  {p.label}
                </button>
              ))}
              {providers.length === 0 && <div className="col-span-3 text-xs text-[#8A8780] p-3">No providers enabled. Configure in Finance Settings.</div>}
            </div>
            <button onClick={createOrder} disabled={!provider || loading} data-testid="btn-create-order"
                    className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <CreditCard size={14} weight="bold"/>{loading ? "Creating…" : "Create payment order"}
            </button>
          </>
        )}

        {tab === "manual" && (
          <div className="space-y-3" data-testid="pm-manual-form">
            <div>
              <ML>Method *</ML>
              <div className="grid grid-cols-4 gap-2">
                {["cash", "upi", "cheque", "bank_transfer"].map(m => (
                  <button key={m} onClick={() => setMMethod(m)}
                          data-testid={`pm-manual-method-${m}`}
                          className={`h-10 rounded-md border text-xs font-medium ${mMethod === m ? "bg-[#2D5A27] border-[#2D5A27] text-white" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
                    {m === "bank_transfer" ? "Bank" : m.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <ML>{mMethod === "cheque" ? "Cheque #" : mMethod === "upi" ? "UPI Ref / RRN" : "Reference / Txn #"}</ML>
                <input value={mTxnNo} onChange={e => setMTxnNo(e.target.value)}
                       placeholder={mMethod === "cash" ? "optional" : "required"}
                       data-testid="pm-manual-txnno"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
              </div>
              <div>
                <ML>Txn Date *</ML>
                <input type="date" value={mTxnDate} onChange={e => setMTxnDate(e.target.value)}
                       data-testid="pm-manual-txndate"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              </div>
              <div>
                <ML>Staff in-charge</ML>
                <input value={mStaff} onChange={e => setMStaff(e.target.value)}
                       placeholder="Field staff who collected"
                       data-testid="pm-manual-staff"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              </div>
              <div>
                <ML>Amount</ML>
                <input type="number" value={mAmount} onChange={e => setMAmount(e.target.value)}
                       placeholder={`Default ₹${Number(invoice.total || 0).toFixed(2)}`}
                       data-testid="pm-manual-amount"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
              </div>
            </div>
            <div>
              <ML>Notes</ML>
              <textarea value={mNotes} onChange={e => setMNotes(e.target.value)} rows={2}
                        placeholder="Anything worth recording"
                        data-testid="pm-manual-notes"
                        className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-xs"/>
            </div>
            <div className="bg-[#F4F8F2] border border-[#D4E0D1] rounded-md p-3 text-xs text-[#2D5A27] flex items-center gap-2">
              <CheckCircle size={14} weight="fill"/>
              Verified by <b>you</b> ({/* will be auto-set on backend from token */} logged-in user)
            </div>
            <button onClick={async () => {
                      if (mMethod !== "cash" && !mTxnNo.trim()) { toast.error("Reference number is required for non-cash"); return; }
                      setMBusy(true);
                      try {
                        const body = {
                          method: mMethod,
                          txn_no: mTxnNo || null,
                          txn_date: mTxnDate,
                          staff_in_charge_name: mStaff || null,
                          amount: mAmount ? Number(mAmount) : null,
                          notes: mNotes || null,
                        };
                        await api.post(`/finance/invoices/${invoice.id}/manual-payment`, body);
                        toast.success("Payment verified");
                        onDone?.(); onClose?.();
                      } catch (e) { toast.error(formatApiError(e)); }
                      setMBusy(false);
                    }}
                    disabled={mBusy} data-testid="pm-manual-submit"
                    className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
              <CheckCircle size={14} weight="bold"/>{mBusy ? "Verifying…" : "Verify & Mark Paid"}
            </button>
          </div>
        )}

        {tab === "gateway" && order && (
          <div>
            <div className="border border-[#E5E2D9] rounded-md p-4 mb-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Order created</div>
              <div className="font-mono text-sm mt-1">{order.transaction.txn_no}</div>
              <div className="text-xs text-[#5C5A55] mt-0.5">Provider: {order.transaction.provider.toUpperCase()} · Demo mode</div>
              <div className="text-xs text-[#5C5A55] mt-0.5 font-mono break-all">{order.checkout.checkout_url}</div>
            </div>
            <div className="bg-[#C25D38]/10 border border-[#C25D38]/30 rounded-md p-3 text-xs text-[#7A2E18] mb-4">
              <b>DEMO MODE:</b> Real provider SDK not wired yet. Click below to simulate a successful gateway webhook.
            </div>
            <button onClick={confirmDemo} data-testid="btn-confirm-demo"
                    className="w-full h-11 bg-[#C25D38] hover:bg-[#A74E2F] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
              <CheckCircle size={14} weight="bold"/>Simulate successful payment
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const ML = ({ children }) => (
  <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5">{children}</label>
);

// Compact icon-only action button for the Invoice row.
// Always visible; disabled state explains "why not" via tooltip.

// Iter 138 — Download menu with paper-size picker.
function DownloadMenu({ inv, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button type="button"
              onClick={() => setOpen(v => !v)}
              data-testid={`inv-pdf-${inv.id}`}
              title="Download invoice PDF (choose paper size)"
              className="w-8 h-8 inline-flex items-center justify-center rounded hover:bg-[#E8EFE6] text-[#2D5A27]">
        <Download size={14}/>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-48 bg-white border border-[#E5E2D9] rounded-md shadow-lg py-1"
             data-testid={`inv-pdf-menu-${inv.id}`}>
          <div className="px-3 py-1 text-[9px] uppercase tracking-[0.2em] text-[#8A8780]">
            Paper size
          </div>
          <button onClick={() => { setOpen(false); onPick("a4"); }}
                  data-testid={`inv-pdf-a4-${inv.id}`}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0F7EE] flex items-center gap-2">
            <span className="w-6 text-[10px] font-mono text-[#8A8780]">A4</span>
            <span>Standard invoice PDF</span>
          </button>
          <button onClick={() => { setOpen(false); onPick("a5"); }}
                  data-testid={`inv-pdf-a5-${inv.id}`}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0F7EE] flex items-center gap-2">
            <span className="w-6 text-[10px] font-mono text-[#8A8780]">A5</span>
            <span>Half-sheet A5 PDF</span>
          </button>
          <button onClick={() => { setOpen(false); onPick("thermal"); }}
                  data-testid={`inv-pdf-thermal-${inv.id}`}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0F7EE] flex items-center gap-2">
            <span className="w-6 text-[10px] font-mono text-[#8A8780]">80mm</span>
            <span>Thermal receipt PDF</span>
          </button>
        </div>
      )}
    </div>
  );
}


function ActionIcon({
  Icon, onClick, title, testid, color = "#5C5A55",
  bg = "hover:bg-[#F0EEE8]", iconWeight, badge, disabled,
}) {
  return (
    <button
      data-testid={testid}
      title={title}
      aria-label={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`relative w-8 h-8 inline-flex items-center justify-center rounded-md transition ${bg} ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
      style={{ color }}
    >
      <Icon size={15} weight={iconWeight} />
      {badge && (
        <span className="absolute -top-1 -right-1 bg-[#1A1A1A] text-white text-[8px] font-mono rounded-full min-w-[14px] h-[14px] px-1 inline-flex items-center justify-center leading-none">
          {badge}
        </span>
      )}
    </button>
  );
}


/* ============================================================
 * SegmentEditor — full sub-invoice form for one consolidated date.
 * ============================================================ */
function SegmentEditor({ idx, seg, packages, wasteTypes, products, charges, fieldStaff, onChange, onRemove, todayIso }) {
  const setWaste = (lines) => onChange({ waste_lines: lines });
  const setProds = (lines) => onChange({ product_lines: lines });
  const toggleExtra = (id) => onChange({
    extra_charge_ids: (seg.extra_charge_ids || []).includes(id)
      ? seg.extra_charge_ids.filter(x => x !== id)
      : [...(seg.extra_charge_ids || []), id],
  });

  return (
    <div className="bg-white border border-[#2D5A27]/30 rounded-md p-4"
          data-testid={`inv-segment-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#2D5A27] font-semibold">
          <span className="bg-[#2D5A27] text-white rounded-full w-5 h-5 inline-flex items-center justify-center text-[10px]">{idx + 1}</span>
          Bill segment
        </div>
        <button type="button" onClick={onRemove}
                data-testid={`inv-segment-remove-${idx}`}
                className="h-7 px-2 border border-[#E5E2D9] rounded text-[11px] text-[#B12A1E] hover:bg-[#FEF1EE]">
          <X size={10} className="inline -mt-0.5 mr-0.5"/>Remove
        </button>
      </div>

      {/* Date · Field staff · Distance */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] block mb-1">Service Date</label>
          <input type="date" value={seg.service_date}
                  onChange={e => onChange({ service_date: e.target.value })}
                  max={todayIso()}
                  data-testid={`inv-segment-date-${idx}`}
                  className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-sm"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] block mb-1">Field Staff</label>
          <select value={seg.field_staff_id || ""}
                  onChange={e => onChange({ field_staff_id: e.target.value })}
                  data-testid={`inv-segment-staff-${idx}`}
                  className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs">
            <option value="">— unassigned —</option>
            {fieldStaff.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] block mb-1">Distance (km)</label>
          <input type="number" min="0" step="0.1" value={seg.distance_km || 0}
                  onChange={e => onChange({ distance_km: Number(e.target.value) })}
                  className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-sm"/>
        </div>
      </div>

      {/* Package */}
      <div className="mb-3">
        <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] block mb-1">Package <span className="text-[#2D5A27] normal-case tracking-normal">· auto from customer</span></label>
        <select value={seg.package_id || ""}
                onChange={e => onChange({ package_id: e.target.value })}
                data-testid={`inv-segment-package-${idx}`}
                className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-sm">
          <option value="">— no package —</option>
          {packages.map(p => (
            <option key={p.id} value={p.id}>
              {p.name} · ₹{Number(p.price).toFixed(0)}{p.gst_percent > 0 ? ` · ${p.gst_percent}% ${p.gst_inclusive ? "incl" : "excl"}` : ""}
            </option>
          ))}
        </select>
      </div>

      {/* Package Inclusions — per-category qty entry from selected package */}
      {seg.package_id && (() => {
        const pkg = packages.find(p => p.id === seg.package_id);
        if (!pkg || !Array.isArray(pkg.items) || pkg.items.length === 0) return null;
        return (
          <div className="mb-3 p-3 rounded-md border border-[#2D5A27]/20 bg-[#F2F7F0]"
                data-testid={`inv-segment-pkg-inclusions-${idx}`}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#2D5A27] font-semibold mb-2">
              Package Inclusions · fill collected qty
            </div>
            <div className="space-y-2">
              {pkg.items.map((pi, k) => (
                <PackageItemRow key={k} pi={pi} segIdx={idx}
                                 wasteTypes={wasteTypes}
                                 wasteLines={seg.waste_lines || []}
                                 setWaste={setWaste}/>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Waste lines */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">Waste items</div>
          <button type="button"
                  onClick={() => setWaste([...(seg.waste_lines || []),
                    { waste_type_id: wasteTypes[0]?.id || "", quantity_kg: 0 }])}
                  className="text-[11px] text-[#2D5A27] inline-flex items-center gap-1">
            <Plus size={9}/>Add waste
          </button>
        </div>
        {(seg.waste_lines || []).length === 0 ? (
          <div className="text-[11px] text-[#8A8780] italic">No waste lines.</div>
        ) : (
          <div className="space-y-1.5">
            {seg.waste_lines.map((wl, j) => (
              <div key={j} className="grid grid-cols-12 gap-2">
                <select value={wl.waste_type_id}
                        onChange={e => setWaste(seg.waste_lines.map((x, k) => k === j ? { ...x, waste_type_id: e.target.value } : x))}
                        className="col-span-7 h-8 px-2 border border-[#E5E2D9] rounded-md text-xs bg-white">
                  {wasteTypes.map(w => <option key={w.id} value={w.id}>{w.name}{w.recyclable ? " · ♻" : ""} (₹{w.rate_per_kg}/kg)</option>)}
                </select>
                <input type="number" min="0" step="0.01" value={wl.quantity_kg || 0}
                        onChange={e => setWaste(seg.waste_lines.map((x, k) => k === j ? { ...x, quantity_kg: Number(e.target.value) } : x))}
                        placeholder="kg"
                        className="col-span-4 h-8 px-2 border border-[#E5E2D9] rounded-md text-xs bg-white"/>
                <button type="button"
                        onClick={() => setWaste(seg.waste_lines.filter((_, k) => k !== j))}
                        className="col-span-1 h-8 border border-[#E5E2D9] rounded text-[#B12A1E]">
                  <X size={10} className="mx-auto"/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Product lines */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">Products</div>
          <button type="button"
                  onClick={() => setProds([...(seg.product_lines || []),
                    { product_id: products[0]?.id || "", quantity: 1 }])}
                  className="text-[11px] text-[#2D5A27] inline-flex items-center gap-1">
            <Plus size={9}/>Add product
          </button>
        </div>
        {(seg.product_lines || []).length === 0 ? (
          <div className="text-[11px] text-[#8A8780] italic">No products.</div>
        ) : (
          <div className="space-y-1.5">
            {seg.product_lines.map((pl, j) => (
              <div key={j} className="grid grid-cols-12 gap-2">
                <select value={pl.product_id}
                        onChange={e => setProds(seg.product_lines.map((x, k) => k === j ? { ...x, product_id: e.target.value } : x))}
                        className="col-span-7 h-8 px-2 border border-[#E5E2D9] rounded-md text-xs bg-white">
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} · ₹{p.price}</option>)}
                </select>
                <input type="number" min="0" step="1" value={pl.quantity || 1}
                        onChange={e => setProds(seg.product_lines.map((x, k) => k === j ? { ...x, quantity: Number(e.target.value) } : x))}
                        placeholder="qty"
                        className="col-span-4 h-8 px-2 border border-[#E5E2D9] rounded-md text-xs bg-white"/>
                <button type="button"
                        onClick={() => setProds(seg.product_lines.filter((_, k) => k !== j))}
                        className="col-span-1 h-8 border border-[#E5E2D9] rounded text-[#B12A1E]">
                  <X size={10} className="mx-auto"/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Extra charges */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1.5">Extra charges</div>
        <div className="flex flex-wrap gap-1.5">
          {charges.map(c => {
            const on = (seg.extra_charge_ids || []).includes(c.id);
            return (
              <button type="button" key={c.id} onClick={() => toggleExtra(c.id)}
                      className={`px-2.5 h-7 rounded text-[11px] border ${on ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "border-[#E5E2D9] text-[#5C5A55]"}`}>
                {c.type} · ₹{c.amount}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * PackageItemRow — one row per package inclusion with category-
 * filtered waste-type selector + qty input. Auto-syncs to
 * seg.waste_lines so the live preview picks up the entry.
 * ============================================================ */
function PackageItemRow({ pi, segIdx, wasteTypes, wasteLines, setWaste }) {
  // Find the matching waste line(s) — by ref_id or by category match
  const matchingTypes = pi.ref_id
    ? wasteTypes.filter(w => w.id === pi.ref_id)
    : pi.category
      ? wasteTypes.filter(w => w.category === pi.category)
      : wasteTypes;

  // Total kg already entered for this item's coverage
  const coveredIds = new Set(matchingTypes.map(w => w.id));
  const myLines = (wasteLines || []).filter(l => coveredIds.has(l.waste_type_id));
  const totalKg = myLines.reduce((s, l) => s + Number(l.quantity_kg || 0), 0);

  // The "primary" line we'll bind a qty input to (single matching ref or
  // first matching line, else create one against the first matching type)
  const primaryIdx = (wasteLines || []).findIndex(l =>
    pi.ref_id ? l.waste_type_id === pi.ref_id
              : coveredIds.has(l.waste_type_id),
  );

  const [pickType, setPickType] = React.useState(
    pi.ref_id || matchingTypes[0]?.id || "",
  );

  const setQty = (newQty) => {
    const n = Number(newQty || 0);
    if (primaryIdx >= 0) {
      // update existing
      setWaste(wasteLines.map((x, k) => k === primaryIdx
        ? { ...x, quantity_kg: n } : x));
    } else if (pickType) {
      // create new
      setWaste([...(wasteLines || []),
        { waste_type_id: pickType, quantity_kg: n }]);
    }
  };

  const allowed = Number(pi.allowed_quantity || 0);
  const overage = Math.max(0, totalKg - allowed);
  const tone = totalKg === 0 ? "border-[#E5E2D9]"
    : overage > 0 ? "border-[#C9A227] bg-amber-50/60"
    : "border-[#2D5A27]/40 bg-emerald-50/30";

  const label = pi.ref_id
    ? (matchingTypes[0]?.waste_name || pi.name)
    : (pi.category || pi.name);

  const currentQty = primaryIdx >= 0
    ? (wasteLines[primaryIdx].quantity_kg || 0) : 0;

  return (
    <div className={`grid grid-cols-12 gap-2 items-center p-2 rounded-md border ${tone}`}
          data-testid={`pkg-incl-row-${segIdx}-${pi.category || pi.ref_id || ""}`}>
      <div className="col-span-5">
        <div className="text-xs font-semibold text-[#1A1A1A]">{label}</div>
        <div className="text-[10px] text-[#5C5A55]">
          allowed {allowed} kg · ₹{pi.excess_rate || 0}/kg over
        </div>
      </div>
      {!pi.ref_id && matchingTypes.length > 1 && (
        <select value={pickType}
                onChange={e => setPickType(e.target.value)}
                className="col-span-4 h-8 px-2 border border-[#E5E2D9] rounded text-[11px] bg-white">
          {matchingTypes.map(w => (
            <option key={w.id} value={w.id}>
              {w.waste_name}
            </option>
          ))}
        </select>
      )}
      <input
        type="number" min="0" step="0.01"
        value={currentQty}
        onChange={e => setQty(e.target.value)}
        placeholder="kg"
        data-testid={`pkg-incl-qty-${segIdx}-${pi.category || pi.ref_id || ""}`}
        className={`${pi.ref_id || matchingTypes.length <= 1 ? "col-span-7" : "col-span-3"} h-8 px-2 border border-[#E5E2D9] rounded text-xs bg-white text-right font-mono`}
      />
      {totalKg > 0 && (
        <div className="col-span-12 text-[10px] text-[#5C5A55] -mt-1 pl-1">
          collected: <span className="font-mono">{totalKg.toFixed(2)} kg</span>
          {overage > 0
            ? <> · <span className="text-[#B12A1E]">{overage.toFixed(2)} kg over → ₹{(overage * Number(pi.excess_rate || 0)).toFixed(2)} excess</span></>
            : <> · <span className="text-[#2D5A27]">within quota</span></>}
        </div>
      )}
    </div>
  );
}

